import { useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RotateCcw } from 'lucide-react';
import {
  DEFAULT_DIPLOMA_LAYOUT,
  normalizeDiplomaLayout,
  type DiplomaLayout,
  type DiplomaLayoutElement,
} from '@/hooks/useCertificateConfig';

type ElementKey = Exclude<keyof DiplomaLayout, 'qr'>;

const ELEMENT_LABELS: Record<ElementKey, { label: string; placeholder: string }> = {
  participantName: { label: 'Nombre del participante', placeholder: '[Nombre del participante]' },
  diplomaName:     { label: 'Nombre del diploma',     placeholder: '[Nombre del diploma]' },
  date:            { label: 'Fecha de emisión',       placeholder: 'Fecha de emisión: 1 de enero de 2026' },
  certificateId:   { label: 'ID de certificado',      placeholder: 'ID: ABCD1234' },
  entities:        { label: 'Entidades emisoras',     placeholder: 'Emitido por: [Entidades]' },
  publicUrl:       { label: 'URL de verificación',    placeholder: 'Verificar: https://…/c/…' },
};

const ELEMENT_ORDER: ElementKey[] = [
  'participantName', 'diplomaName', 'date', 'certificateId', 'entities', 'publicUrl',
];

interface Props {
  imageUrl: string | null;
  layout: DiplomaLayout;
  onChange: (next: DiplomaLayout) => void;
}

export function DiplomaLayoutEditor({ imageUrl, layout, onChange }: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{ key: ElementKey | 'qr' } | null>(null);
  const safe = normalizeDiplomaLayout(layout);

  const updateEl = useCallback((key: ElementKey, patch: Partial<DiplomaLayoutElement>) => {
    onChange({ ...safe, [key]: { ...safe[key], ...patch } });
  }, [safe, onChange]);

  const updateQr = useCallback((patch: Partial<DiplomaLayout['qr']>) => {
    onChange({ ...safe, qr: { ...safe.qr, ...patch } });
  }, [safe, onChange]);

  const handleMove = useCallback((e: MouseEvent) => {
    if (!dragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    if (dragging.key === 'qr') updateQr({ x, y });
    else updateEl(dragging.key, { x, y });
  }, [dragging, updateEl, updateQr]);

  const startDrag = (key: ElementKey | 'qr') => (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging({ key });
    const onMove = (ev: MouseEvent) => handleMove(ev);
    const onUp = () => {
      setDragging(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const reset = () => onChange(DEFAULT_DIPLOMA_LAYOUT);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Posiciones de los elementos</CardTitle>
        <Button variant="outline" size="sm" onClick={reset}>
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restablecer
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Arrastra cada elemento sobre la imagen para colocarlo donde quieras. Ajusta tamaño, color y alineación desde la lista.
        </p>

        {/* Canvas */}
        <div
          ref={canvasRef}
          className="relative w-full bg-muted rounded-lg overflow-hidden border border-border select-none"
          style={{ aspectRatio: '297 / 210' }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt="Diploma base" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
              Sube una imagen base para previsualizar
            </div>
          )}

          {ELEMENT_ORDER.map((key) => {
            const el = safe[key];
            if (!el.enabled) return null;
            const transform =
              el.align === 'center' ? 'translate(-50%, -50%)'
              : el.align === 'right' ? 'translate(-100%, -50%)'
              : 'translate(0, -50%)';
            return (
              <div
                key={key}
                onMouseDown={startDrag(key)}
                className="absolute cursor-move whitespace-nowrap px-1 rounded hover:outline hover:outline-2 hover:outline-primary"
                style={{
                  left: `${el.x}%`,
                  top: `${el.y}%`,
                  transform,
                  fontSize: `${Math.max(8, el.fontSize * 0.55)}px`,
                  color: el.color,
                  fontWeight: key === 'participantName' ? 700 : 400,
                  textShadow: '0 1px 2px rgba(255,255,255,0.6)',
                }}
              >
                {ELEMENT_LABELS[key].placeholder}
              </div>
            );
          })}

          {safe.qr.enabled && (
            <div
              onMouseDown={startDrag('qr')}
              className="absolute cursor-move bg-white border-2 border-dashed border-primary flex items-center justify-center text-[10px] text-primary font-mono"
              style={{
                left: `${safe.qr.x}%`,
                top: `${safe.qr.y}%`,
                width: `${(safe.qr.size / 297) * 100}%`,
                aspectRatio: '1 / 1',
                transform: 'translate(-50%, -50%)',
              }}
            >
              QR
            </div>
          )}
        </div>

        {/* Element controls */}
        <div className="space-y-3">
          {ELEMENT_ORDER.map((key) => {
            const el = safe[key];
            return (
              <div key={key} className="border border-border rounded-lg p-3 space-y-2 bg-background/50">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">{ELEMENT_LABELS[key].label}</Label>
                  <Switch checked={el.enabled} onCheckedChange={(v) => updateEl(key, { enabled: v })} />
                </div>
                {el.enabled && (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                    <div>
                      <Label className="text-xs text-muted-foreground">X (%)</Label>
                      <Input type="number" min={0} max={100} value={Math.round(el.x)} onChange={(e) => updateEl(key, { x: Number(e.target.value) })} className="h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Y (%)</Label>
                      <Input type="number" min={0} max={100} value={Math.round(el.y)} onChange={(e) => updateEl(key, { y: Number(e.target.value) })} className="h-8" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs text-muted-foreground">Tamaño ({el.fontSize}pt)</Label>
                      <Slider value={[el.fontSize]} min={6} max={72} step={1} onValueChange={([v]) => updateEl(key, { fontSize: v })} className="mt-2" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Color</Label>
                      <input
                        type="color"
                        value={el.color}
                        onChange={(e) => updateEl(key, { color: e.target.value })}
                        className="h-8 w-full rounded border border-input bg-background cursor-pointer"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Alineación</Label>
                      <Select value={el.align} onValueChange={(v) => updateEl(key, { align: v as 'left' | 'center' | 'right' })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">Izquierda</SelectItem>
                          <SelectItem value="center">Centro</SelectItem>
                          <SelectItem value="right">Derecha</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* QR */}
          <div className="border border-border rounded-lg p-3 space-y-2 bg-background/50">
            <div className="flex items-center justify-between">
              <Label className="font-medium">Código QR</Label>
              <Switch checked={safe.qr.enabled} onCheckedChange={(v) => updateQr({ enabled: v })} />
            </div>
            {safe.qr.enabled && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <Label className="text-xs text-muted-foreground">X (%)</Label>
                  <Input type="number" min={0} max={100} value={Math.round(safe.qr.x)} onChange={(e) => updateQr({ x: Number(e.target.value) })} className="h-8" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Y (%)</Label>
                  <Input type="number" min={0} max={100} value={Math.round(safe.qr.y)} onChange={(e) => updateQr({ y: Number(e.target.value) })} className="h-8" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Tamaño ({safe.qr.size}mm)</Label>
                  <Slider value={[safe.qr.size]} min={15} max={50} step={1} onValueChange={([v]) => updateQr({ size: v })} className="mt-2" />
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
